import React from 'react';
import { Building2, Palette, Image as ImageIcon, Clock, Save, Check, X, Award, Sparkles, ChevronRight, Coffee, Store, Phone, Share2, Instagram, Twitter, Linkedin, MapPin, Heart } from 'lucide-react';
import { slugify } from '../../../../utils/slugify';
import { useAlert } from '../../../../context/AlertContext';

const CompanyProfile = ({ t, tenantData, canEdit, profileDraft, setProfileDraft, handleSaveProfile, isSavingProfile, handleLogoUpload, handleBannerUpload, uploading, bannerUploading }) => {
    const { showConfirm } = useAlert();

    const handleSaveWithConfirm = async () => {
        const confirmed = await showConfirm(
            'İşletme profil bilgilerini güncellemek istediğinize emin misiniz?',
            { title: 'Profili Kaydet', confirmText: 'Kaydet', type: 'info' }
        );
        if (!confirmed) return;
        handleSaveProfile();
    };

    const handleWorkingHourChange = (index, field, value) => {
        const newHours = [...(profileDraft.workingHours || [])];
        newHours[index][field] = value;
        setProfileDraft({ ...profileDraft, workingHours: newHours });
    };

    const updateDraft = (field, value) => {
        setProfileDraft({ ...profileDraft, [field]: value });
    };

    const updateSocial = (field, value) => {
        setProfileDraft({
            ...profileDraft,
            socialMedia: { ...profileDraft.socialMedia, [field]: value }
        });
    };

    return (
    <div className="company-profile-container">
        <style>{`
            .company-profile-container {
                display: grid;
                grid-template-columns: 1fr 340px;
                gap: 2rem;
                align-items: start;
                overflow: hidden;
                min-width: 0;
            }
            .company-profile-container .card {
                overflow: hidden;
                min-width: 0;
            }
            .company-profile-container input,
            .company-profile-container textarea,
            .company-profile-container select {
                box-sizing: border-box;
                max-width: 100%;
            }
            .profile-main-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 3rem;
                min-width: 0;
            }
            .profile-main-grid > div {
                min-width: 0;
            }
            .working-hours-column {
                border-left: 1px solid var(--border);
                padding-left: 3rem;
                min-width: 0;
            }
            /* Let each day row wrap instead of overflowing (and getting clipped
               by the container's overflow:hidden) when the column is narrow —
               e.g. while the phone preview is still shown on mid-size screens. */
            .working-hour-row {
                flex-wrap: wrap;
                row-gap: 0.5rem;
                min-width: 0;
            }
            .working-hour-row > .wh-times {
                flex-wrap: wrap;
                min-width: 0;
            }
            .working-hour-row input[type="time"] {
                min-width: 0;
                max-width: 100%;
            }
            .logo-banner-flex {
                display: flex;
                gap: 1.5rem;
                flex-wrap: wrap;
            }
            
            @media (max-width: 1150px) {
                .company-profile-container {
                    grid-template-columns: 1fr;
                }
                .profile-preview-aside {
                    position: relative !important;
                    top: 0 !important;
                    display: none; /* Hide preview on tablet/mobile to focus on editing */
                }
                .settings-header-actions { width: 100% !important; flex-direction: row !important; justify-content: flex-start !important; }
                .settings-header-actions button { flex: 1; align-items: center; justify-content: center; }
            }
            
            @media (max-width: 850px) {
                .profile-main-grid {
                    grid-template-columns: 1fr;
                    gap: 2rem;
                }
                .working-hours-column {
                    border-left: none;
                    padding-left: 0;
                    border-top: 1px solid var(--border);
                    padding-top: 2rem;
                }
                .social-grid {
                    grid-template-columns: 1fr !important;
                }
            }
            
            @media (max-width: 480px) {
                .company-profile-container .card.glass {
                    padding: 0.875rem;
                    border-radius: 14px;
                }
                .company-profile-container h2 {
                    font-size: 1.1rem !important;
                }
                .logo-banner-flex {
                    flex-direction: column;
                }
                .logo-banner-flex .banner-upload-label {
                    width: 100% !important;
                    min-width: 0 !important;
                }
                .working-hour-row {
                    flex-direction: column;
                    align-items: flex-start !important;
                    gap: 0.75rem !important;
                }
                .working-hour-row .wh-times {
                    width: 100%;
                    flex-wrap: nowrap;
                }
                .working-hour-row input[type="time"] {
                    width: 100% !important;
                }
                .logo-upload-label div {
                    width: 100% !important;
                    max-width: 120px !important;
                    height: 120px !important;
                }
                .banner-upload-label div {
                    max-width: 100% !important;
                }
                .social-grid {
                    grid-template-columns: 1fr !important;
                }
                .profile-main-grid label {
                    font-size: 0.8rem !important;
                }
                .profile-main-grid {
                    gap: 1.5rem !important;
                }
            }
        `}</style>

    <section className="card glass">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Building2 size={24} style={{ color: 'var(--primary)' }} />
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('settings.companyProfile')}</h2>
            </div>
            {canEdit && (
                <div className="settings-header-actions" style={{ display: 'flex', gap: '8px' }}>
                    <button 
                        onClick={() => {
                            setProfileDraft({
                                name: tenantData.name || '',
                                slug: tenantData.slug || '',
                                industry: tenantData.industry || '',
                                phone: tenantData.phone || '',
                                socialMedia: tenantData.social_media || tenantData.socialMedia || {},
                                workingHours: tenantData.working_hours || tenantData.workingHours || [
                                    { day: 'Pazartesi', open: '08:00', close: '22:00', active: true },
                                    { day: 'Salı', open: '08:00', close: '22:00', active: true },
                                    { day: 'Çarşamba', open: '08:00', close: '22:00', active: true },
                                    { day: 'Perşembe', open: '08:00', close: '22:00', active: true },
                                    { day: 'Cuma', open: '08:00', close: '22:00', active: true },
                                    { day: 'Cumartesi', open: '08:00', close: '22:00', active: true },
                                    { day: 'Pazar', open: '08:00', close: '22:00', active: true }
                                ],
                                city: tenantData.city || '',
                                district: tenantData.district || '',
                                address: tenantData.address || '',
                                googleMapsUrl: tenantData.googleMapsUrl || tenantData.google_maps_url || ''
                            });
                        }}
                        style={{ padding: '8px 16px', borderRadius: '12px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-main)', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                        Vazgeç
                    </button>
                    <button 
                        onClick={handleSaveWithConfirm}
                        disabled={isSavingProfile}
                        className="btn-primary"
                        style={{ padding: '10px 24px', borderRadius: '14px', border: 'none', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700 }}
                    >
                        <Save size={18} />
                        {isSavingProfile ? t('common.saving') : t('common.save')}
                    </button>
                </div>
            )}
        </div>

        <div className="profile-main-grid">
            {/* Left Column: Profile Info */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                <div className="logo-banner-flex">
                    {/* Logo Upload */}
                    <div className="logo-upload-label" style={{ position: 'relative' }}>
                        <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.5rem' }}>LOGO</p>
                        <label style={{ cursor: 'pointer', display: 'block', position: 'relative' }}>
                            <div style={{ width: '140px', height: '140px', background: 'var(--bg-main)', border: '2px solid var(--border)', borderRadius: '16px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                {tenantData?.theme?.logoUrl ? <img src={tenantData.theme.logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Building2 size={32} color="var(--text-muted)" />}
                                <div className="hover-overlay" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s ease', color: 'white', fontSize: '0.75rem', fontWeight: 700 }}>
                                    {uploading ? '...' : 'DĞŞ'}
                                </div>
                            </div>
                            <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} disabled={uploading} />
                        </label>
                    </div>

                    {/* Banner Upload */}
                    <div className="banner-upload-label" style={{ flex: 1, minWidth: 0, width: '100%' }}>
                        <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.5rem' }}>BANNER</p>
                        <label style={{ cursor: 'pointer', display: 'block', position: 'relative' }}>
                            <div style={{ aspectRatio: '2 / 1', maxHeight: '200px', background: 'var(--bg-main)', border: '2px solid var(--border)', borderRadius: '16px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                {tenantData?.banner_url ? (
                                    <img src={tenantData.banner_url} alt="Banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                        <ImageIcon size={24} color="var(--text-muted)" />
                                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600 }}>2:1 oran (ör. 800×400)</span>
                                    </div>
                                )}
                                <div className="hover-overlay" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s ease', color: 'white', fontSize: '0.75rem', fontWeight: 700 }}>
                                    {bannerUploading ? '...' : 'KAPAK DEĞİŞTİR'}
                                </div>
                            </div>
                            <input type="file" accept="image/*" onChange={handleBannerUpload} style={{ display: 'none' }} disabled={bannerUploading} />
                        </label>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '1.25rem' }}>
                    <div style={{ gridColumn: 'span 2' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>{t('settings.companyName')}</label>
                        <input type="text" disabled={!canEdit} value={profileDraft.name} onChange={e => updateDraft('name', e.target.value)} style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '12px', background: 'var(--bg-sidebar)', color: 'var(--text-main)', outline: 'none' }} />
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                         <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }} className="social-grid">
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>{t('settings.industry')}</label>
                                <select disabled={!canEdit} value={profileDraft.industry} onChange={e => updateDraft('industry', e.target.value)} style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '12px', background: 'var(--bg-sidebar)', color: 'var(--text-main)', outline: 'none' }}>
                                    <option value="" disabled>{t('setup.selectIndustry')}</option>
                                    <option value="cafe">{t('setup.industries.cafe')}</option>
                                    <option value="patisserie">{t('setup.industries.patisserie')}</option>
                                    <option value="restaurant">{t('setup.industries.restaurant')}</option>
                                    <option value="bakery">{t('setup.industries.bakery')}</option>
                                    <option value="other">{t('setup.industries.other')}</option>
                                </select>
                            </div>
                            {/* Slug (Web Adresi) alanı kullanıcı isteği üzerine kaldırıldı. Slug otomatik oluşturulmaktadır. */}
                         </div>
                    </div>
                </div>

                {/* Contact Info Sub-section */}
                <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border)', paddingTop: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem' }}>
                        <Phone size={18} style={{ color: 'var(--primary)' }} />
                        <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>İletişim Bilgileri</h3>
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-muted)' }}>Telefon Numarası</label>
                        <input type="text" placeholder="5XX XXX XX XX" value={profileDraft.phone} onChange={e => updateDraft('phone', e.target.value)} style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '12px', background: 'var(--bg-sidebar)', color: 'var(--text-main)', outline: 'none' }} />
                    </div>
                </div>

                {/* Social Media Sub-section */}
                <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border)', paddingTop: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem' }}>
                        <Share2 size={18} style={{ color: 'var(--primary)' }} />
                        <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Sosyal Medya</h3>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }} className="social-grid">
                        <div style={{ position: 'relative' }}>
                            <Instagram size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input type="text" placeholder="Instagram" value={profileDraft.socialMedia?.instagram || ''} onChange={e => updateSocial('instagram', e.target.value)} style={{ width: '100%', padding: '0.6rem 1rem 0.6rem 2.2rem', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--bg-sidebar)', color: 'var(--text-main)', outline: 'none', fontSize: '0.8rem' }} />
                        </div>
                        <div style={{ position: 'relative' }}>
                            <Twitter size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input type="text" placeholder="X (Twitter)" value={profileDraft.socialMedia?.x || ''} onChange={e => updateSocial('x', e.target.value)} style={{ width: '100%', padding: '0.6rem 1rem 0.6rem 2.2rem', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--bg-sidebar)', color: 'var(--text-main)', outline: 'none', fontSize: '0.8rem' }} />
                        </div>
                        <div style={{ position: 'relative' }}>
                            <Linkedin size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input type="text" placeholder="LinkedIn" value={profileDraft.socialMedia?.linkedin || ''} onChange={e => updateSocial('linkedin', e.target.value)} style={{ width: '100%', padding: '0.6rem 1rem 0.6rem 2.2rem', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--bg-sidebar)', color: 'var(--text-main)', outline: 'none', fontSize: '0.8rem' }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Column: Working Hours */}
            <div className="working-hours-column">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '2rem' }}>
                    <Clock size={24} style={{ color: 'var(--primary)' }} />
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Çalışma Saatleri</h2>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {(profileDraft.workingHours || []).map((wh, idx) => (
                        <div key={wh.day} className="working-hour-row" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.5rem 1rem', background: wh.active ? 'rgba(0,0,0,0.02)' : 'rgba(0,0,0,0.01)', borderRadius: '12px', opacity: wh.active ? 1 : 0.5 }}>
                            <div style={{ width: '80px', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0 }}>{wh.day}</div>

                            <div className="wh-times" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <input
                                    type="time"
                                    disabled={!wh.active || !canEdit}
                                    value={wh.open}
                                    onChange={e => handleWorkingHourChange(idx, 'open', e.target.value)}
                                    style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.8rem', background: 'white' }}
                                />
                                <span style={{ color: 'var(--text-muted)' }}>-</span>
                                <input
                                    type="time"
                                    disabled={!wh.active || !canEdit}
                                    value={wh.close}
                                    onChange={e => handleWorkingHourChange(idx, 'close', e.target.value)}
                                    style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.8rem', background: 'white' }}
                                />
                            </div>

                            <button
                                onClick={() => handleWorkingHourChange(idx, 'active', !wh.active)}
                                style={{
                                    marginLeft: 'auto',
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: wh.active ? '#dcfce7' : '#fee2e2',
                                    color: wh.active ? '#15803d' : '#b91c1c',
                                    fontSize: '0.65rem',
                                    fontWeight: 800,
                                    cursor: 'pointer'
                                }}
                            >
                                {wh.active ? 'AÇIK' : 'KAPALI'}
                            </button>
                        </div>
                    ))}
                </div>

                {/* Location Section */}
                <div style={{ marginTop: '2.5rem', borderTop: '1px solid var(--border)', paddingTop: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem' }}>
                        <MapPin size={20} style={{ color: 'var(--primary)' }} />
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 700 }}>İşletme Konumu</h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }} className="social-grid">
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>İl</label>
                            <input 
                                type="text" 
                                placeholder="Örn: İstanbul" 
                                value={profileDraft.city} 
                                onChange={e => {
                                    const val = e.target.value;
                                    const formatted = val.split(' ').map(word => {
                                        if (!word) return '';
                                        const first = word[0] === 'i' ? 'İ' : word[0].toUpperCase();
                                        return first + word.slice(1).replace(/I/g, 'ı').toLowerCase();
                                    }).join(' ');
                                    updateDraft('city', formatted);
                                }}
                                style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--bg-sidebar)', color: 'var(--text-main)', outline: 'none', fontSize: '0.85rem' }} 
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>İlçe</label>
                            <input 
                                type="text" 
                                placeholder="Örn: Beşiktaş" 
                                value={profileDraft.district} 
                                onChange={e => {
                                    const val = e.target.value;
                                    const formatted = val.split(' ').map(word => {
                                        if (!word) return '';
                                        const first = word[0] === 'i' ? 'İ' : word[0].toUpperCase();
                                        return first + word.slice(1).replace(/I/g, 'ı').toLowerCase();
                                    }).join(' ');
                                    updateDraft('district', formatted);
                                }}
                                style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--bg-sidebar)', color: 'var(--text-main)', outline: 'none', fontSize: '0.85rem' }} 
                            />
                        </div>
                    </div>

                    <div>
                        <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Açık Adres</label>
                        <textarea 
                            rows={2} 
                            placeholder="Sokak, mahalle, no..." 
                            value={profileDraft.address} 
                            onChange={e => {
                                const val = e.target.value;
                                const formatted = val.split(' ').map(word => {
                                    if (!word) return '';
                                    const first = word[0] === 'i' ? 'İ' : word[0].toUpperCase();
                                    return first + word.slice(1).replace(/I/g, 'ı').toLowerCase();
                                }).join(' ');
                                updateDraft('address', formatted);
                            }}
                            style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--bg-sidebar)', color: 'var(--text-main)', outline: 'none', fontSize: '0.85rem', resize: 'none', fontFamily: 'inherit' }} 
                        />
                    </div>

                    <div style={{ marginTop: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Google Maps Linki</label>
                        <div style={{ position: 'relative' }}>
                            <MapPin size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary)' }} />
                            <input 
                                type="text" 
                                placeholder="https://goo.gl/maps/..." 
                                value={profileDraft.googleMapsUrl} 
                                onChange={e => updateDraft('googleMapsUrl', e.target.value)} 
                                style={{ width: '100%', padding: '0.6rem 1rem 0.6rem 2.2rem', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--bg-sidebar)', color: 'var(--text-main)', outline: 'none', fontSize: '0.8rem' }} 
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <style>{` .logo-upload-label:hover .hover-overlay, .banner-upload-label:hover .hover-overlay { opacity: 1 !important; } `}</style>
    </section>

    {/* Customer View Preview */}
    <aside className="profile-preview-aside" style={{ position: 'sticky', top: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <p style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>MÜŞTERİ PANELİ ÖNİZLEME</p>

        {/* Phone Frame */}
        <div style={{
            width: '360px',
            height: '740px',
            background: '#111827',
            borderRadius: '40px',
            padding: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 0 0 2px #374151',
            position: 'relative'
        }}>
            {/* Speaker/Camera Notch */}
            <div style={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', width: '120px', height: '28px', background: '#111827', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ width: '40px', height: '4px', background: '#374151', borderRadius: '4px' }}></div>
            </div>

            {/* Screen Content */}
            <div style={{
                width: '100%',
                height: '100%',
                background: '#f5f5f7',
                borderRadius: '32px',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
            }} className="hide-scrollbar">
            {/* Portal Header - matches GlobalCustomerPortal */}
            <div style={{
                background: 'var(--primary)',
                padding: '28px 16px 18px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                position: 'relative',
                flexShrink: 0,
                minHeight: '120px'
            }}>
                <div style={{
                    width: '44px', height: '44px', borderRadius: '14px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '6px'
                }}>
                    <img src="/favicon.svg" alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }} />
                </div>
                <h2 style={{
                    fontSize: '1.5rem', fontWeight: 900, color: 'white',
                    letterSpacing: '-1px', margin: 0
                }}>
                    Ugra
                </h2>
            </div>

            {/* Cards Mockup */}
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '20px',
                    background: '#ffffff',
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 8px 24px -8px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        aspectRatio: '2 / 1',
                        maxHeight: '120px',
                        background: tenantData?.banner_url ? `url(${tenantData.banner_url}) center/cover` : 'linear-gradient(135deg, var(--primary) 0%, #a69088 100%)',
                        position: 'relative'
                    }}>
                        {/* Heart icon */}
                        <div style={{ position: 'absolute', top: '8px', left: '8px', width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                            <Heart size={14} color="#9ca3af" />
                        </div>
                        {(() => {
                            if (!profileDraft.workingHours || profileDraft.workingHours.length === 0) return null;
                            const daysTr = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
                            const now = new Date();
                            const todayTr = daysTr[now.getDay()];
                            const wh = profileDraft.workingHours.find(w => w.day === todayTr);
                            
                            let isOpen = false;
                            if (wh && wh.active && wh.open && wh.close) {
                                const currentTime = now.getHours() * 60 + now.getMinutes();
                                const [openH, openM] = wh.open.split(':').map(Number);
                                const [closeH, closeM] = wh.close.split(':').map(Number);
                                const openTime = openH * 60 + (openM || 0);
                                const closeTime = closeH * 60 + (closeM || 0);
                                if (closeTime < openTime) {
                                    // Gece yarısını geçen saat (örn. 08:00 - 04:00)
                                    isOpen = currentTime >= openTime || currentTime < closeTime;
                                } else {
                                    isOpen = currentTime >= openTime && currentTime <= closeTime;
                                }
                            }
                            // Dünün gece yarısı geçişini de kontrol et
                            if (!isOpen) {
                                const yesterdayIdx = (now.getDay() + 6) % 7;
                                const yesterdayTr = daysTr[yesterdayIdx];
                                const whYesterday = profileDraft.workingHours.find(w => w.day === yesterdayTr);
                                if (whYesterday && whYesterday.active && whYesterday.open && whYesterday.close) {
                                    const currentTime = now.getHours() * 60 + now.getMinutes();
                                    const [oH, oM] = whYesterday.open.split(':').map(Number);
                                    const [cH, cM] = whYesterday.close.split(':').map(Number);
                                    const oT = oH * 60 + (oM || 0);
                                    const cT = cH * 60 + (cM || 0);
                                    if (cT < oT && currentTime < cT) {
                                        isOpen = true;
                                    }
                                }
                            }
                            
                            return (
                                <div style={{ 
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    padding: '4px 8px', 
                                    borderRadius: '8px', 
                                    fontSize: '0.65rem', 
                                    fontWeight: 900, 
                                    background: isOpen ? 'rgba(220, 252, 231, 0.9)' : 'rgba(254, 226, 226, 0.9)', 
                                    color: isOpen ? '#15803d' : '#b91c1c',
                                    backdropFilter: 'blur(4px)',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    zIndex: 10
                                }}>
                                    {isOpen ? 'AÇIK' : 'KAPALI'}
                                </div>
                            );
                        })()}
                    </div>
                    <div style={{ padding: '24px 12px 12px', position: 'relative' }}>
                        <div style={{
                            position: 'absolute',
                            top: '-24px',
                            left: '12px',
                            width: '48px',
                            height: '48px',
                            background: '#ffffff',
                            borderRadius: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            border: '2px solid #ffffff'
                        }}>
                            {tenantData?.theme?.logoUrl ? (
                                <img src={tenantData.theme.logoUrl} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="" />
                            ) : (
                                <Store color="var(--primary)" size={20} />
                            )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <h4 style={{ fontWeight: 900, fontSize: '0.95rem', margin: 0 }}>{profileDraft.name || 'İşletme Adı'}</h4>
                                    {profileDraft.socialMedia?.instagram && (
                                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                                            <Instagram size={12} />
                                        </div>
                                    )}
                                    {profileDraft.googleMapsUrl && (
                                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#4285F4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                                            <MapPin size={12} />
                                        </div>
                                    )}
                                </div>
                                

                                <div style={{ display: 'flex', gap: '4px' }}>
                                    <div style={{ background: 'var(--primary)15', color: 'var(--primary)', padding: '2px 8px', borderRadius: '6px', fontSize: '0.65rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <Award size={10} /> 85 Puan
                                    </div>
                                    <div style={{ background: '#f3f4f6', color: '#666', padding: '2px 8px', borderRadius: '6px', fontSize: '0.65rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <Sparkles size={10} /> Silver
                                    </div>
                                </div>
                            </div>
                            <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ChevronRight size={16} />
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Secondary card for context */}
                <div style={{ padding: '12px', border: '1px dashed var(--border)', borderRadius: '16px', opacity: 0.3, display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--border)' }}></div>
                    <div style={{ flex: 1 }}>
                        <div style={{ height: '10px', width: '40%', background: 'var(--border)', borderRadius: '5px', marginBottom: '6px' }}></div>
                        <div style={{ height: '6px', width: '20%', background: 'var(--border)', borderRadius: '3px' }}></div>
                    </div>
                </div>
            </div>
            
            {/* Bottom Nav Mockup - floating island like CustomerPortal */}
            <div style={{ marginTop: 'auto', padding: '0 12px 12px', flexShrink: 0 }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-around', alignItems: 'center',
                    background: 'var(--primary)', borderRadius: '20px', padding: '10px 8px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)'
                }}>
                    {[
                        { label: 'Menü', active: true },
                        { label: 'Sepet', active: false },
                        { label: 'İndirim', active: false },
                        { label: 'Ödüller', active: false },
                    ].map((tab, i) => (
                        <div key={i} style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                            padding: tab.active ? '4px 12px' : '4px 8px',
                            borderRadius: '12px',
                            background: tab.active ? 'rgba(255,255,255,0.2)' : 'transparent'
                        }}>
                            <div style={{ width: '18px', height: '18px', borderRadius: '5px', background: tab.active ? 'white' : 'rgba(255,255,255,0.3)' }}></div>
                            <span style={{ fontSize: '0.5rem', fontWeight: 700, color: tab.active ? 'white' : 'rgba(255,255,255,0.5)' }}>{tab.label}</span>
                        </div>
                    ))}
                </div>
            </div>
            </div>{/* End Screen Content */}

            {/* Home Indicator */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '8px', paddingBottom: '4px' }}>
                <div style={{ width: '100px', height: '4px', background: '#374151', borderRadius: '4px' }}></div>
            </div>
        </div>{/* End Phone Frame */}

    </aside>
    </div>

    );
};
export default CompanyProfile;